import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { gql } from 'apollo-angular';

import {
    AddToCartMutation,
    AddToCartMutationVariables,
    AdjustItemQuantityMutation,
    AdjustItemQuantityMutationVariables,
    RemoveItemFromCartMutation,
    RemoveItemFromCartMutationVariables,
    GetProductDetailQuery,
    GetProductDetailQueryVariables,
    SearchProductsQuery,
} from '../../../common/generated-types';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { DataService } from '../../providers/data/data.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { StateService } from '../../providers/state/state.service';

import { ADD_TO_CART, ADD_TO_CART_WITH_INSTALLATION, GET_PRODUCT_DETAIL, GET_DISCOUNTED_PRODUCTS } from './product-detail.graphql';
import { ADJUST_ITEM_QUANTITY, REMOVE_ITEM_FROM_CART } from '../cart-drawer/cart-drawer.graphql';
import { ActiveService } from '../../providers/active/active.service';
import { WishlistService } from '../../providers/wishlist/wishlist.service';
import { CompareService } from '../../providers/compare/compare.service';
import { LanguageService } from '../../providers/language/language.service';
import { environment } from '../../../../environments/environment';
import { ModalService } from '../../providers/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { AddToCartDialogComponent } from '../../../shared/components/add-to-cart-dialog/add-to-cart-dialog.component';
import { CompareDialogComponent } from '../../../shared/components/compare-dialog/compare-dialog.component';

type Product = NonNullable<GetProductDetailQuery['product']>;
type Variant = Product['variants'][number];
type Collection = Product['collections'][number];
type OptionGroup = { id: string; code: string; name: string };

const SEARCH_PRODUCTS = gql`
    query SearchProducts($input: SearchInput!) {
        search(input: $input) {
            items {
                productId
                productVariantId
                slug
                productName
                description
                priceWithTax {
                    ... on PriceRange {
                        min
                        max
                    }
                }
                productAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                }
            }
            totalItems
        }
    }
`;

@Component({
    selector: 'vsf-product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnInit, OnDestroy {

    product: GetProductDetailQuery['product'];
    selectedAsset: { id: string; preview: string; };
    qtyInCart: { [id: string]: number; } = {};
    orderLineIds: { [variantId: string]: string; } = {};
    selectedVariant: Variant;
    qty = 1;
    breadcrumbs: Collection['breadcrumbs'] | null = null;
    inFlightVariantId: string | null = null;
    specsOpen = true;
    specCategories: { category: string; specs: { name: string; value: string }[] }[] = [];
    activeSpecTab = 0;
    relatedProducts$: Observable<SearchProductsQuery['search']['items']>;
    inWishlist = false;
    inCompare = false;
    compareCount = 0;
    signedIn = false;
    installationSelected = false;
    variantDiscounts: { [variantId: string]: number } = {};
    optionGroups: OptionGroup[] = [];
    variantFilters: { [groupId: string]: string } = {};
    @ViewChild('addedToCartTemplate', {static: true})
    private addToCartTemplate: TemplateRef<any>;
    private sub: Subscription;
    private wishlistSub: Subscription;
    private compareSub: Subscription;

    constructor(private dataService: DataService,
                private stateService: StateService,
                private notificationService: NotificationService,
                private activeService: ActiveService,
                private wishlistService: WishlistService,
                private compareService: CompareService,
                private languageService: LanguageService,
                private modalService: ModalService,
                private translateService: TranslateService,
                private cdr: ChangeDetectorRef,
                private route: ActivatedRoute,
                private router: Router) {
    }

    ngOnInit() {
        const lastCollectionSlug$ = this.stateService.select(state => state.lastCollectionSlug);
        const productSlug$ = this.route.paramMap.pipe(
            map(paramMap => paramMap.get('slug')),
            filter(notNullOrUndefined),
        );

        this.sub = productSlug$.pipe(
            switchMap(slug => {
                return this.dataService.query<GetProductDetailQuery, GetProductDetailQueryVariables>(GET_PRODUCT_DETAIL, {
                        slug,
                    },
                );
            }),
            map(data => data.product),
            filter(notNullOrUndefined),
            withLatestFrom(lastCollectionSlug$),
        ).subscribe(([product, lastCollectionSlug]) => {
            this.product = product;
            if (this.product.featuredAsset) {
                this.selectedAsset = this.product.featuredAsset;
            }
            this.selectedVariant = product.variants[0];
            // Re-check wishlist state now that selectedVariant is set
            this.inWishlist = this.wishlistService.isInWishlistSync(this.selectedVariant.id);
            this.optionGroups = (product as any).optionGroups || [];
            this.variantFilters = {};
            const raw = (product as any).customFields?.specifications;
            this.specCategories = this.parseSpecs(raw);
            // Fallback: if no specs for current language, fetch English specs
            if (this.specCategories.length === 0 && this.languageService.currentLanguage !== 'en') {
                const { apiHost, apiPort, shopApiPath } = environment;
                const enUrl = `${apiHost}${apiPort ? ':' + apiPort : ''}/${shopApiPath}`;
                fetch(enUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: `query($id: ID!) { product(id: $id) { customFields { specifications } } }`,
                        variables: { id: product.id },
                    }),
                }).then(r => r.json()).then(data => {
                    const enRaw = data?.data?.product?.customFields?.specifications;
                    if (enRaw) {
                        this.specCategories = this.parseSpecs(enRaw);
                        this.activeSpecTab = 0;
                        this.cdr.markForCheck();
                    }
                }).catch(() => {});
            }
            this.activeSpecTab = 0;
            const collection = this.getMostRelevantCollection(product.collections, lastCollectionSlug);
            this.breadcrumbs = collection ? collection.breadcrumbs : [];

            // Load related products from the same collection
            const collectionSlug = collection?.slug;
            const searchInput: any = { take: 5, groupByProduct: true };
            if (collectionSlug) {
                searchInput.collectionSlug = collectionSlug;
            }
            const fetchPolicy = this.languageService.currentLanguage !== 'en' ? 'no-cache' as const : undefined;
            this.relatedProducts$ = this.dataService.query<SearchProductsQuery>(SEARCH_PRODUCTS, {
                input: searchInput,
            }, fetchPolicy).pipe(
                map(data => data.search.items.filter(item => item.productId !== product.id).slice(0, 4)),
            );
        });

        // Fetch discount info for this product
        productSlug$.pipe(
            switchMap(() => this.dataService.query<any>(GET_DISCOUNTED_PRODUCTS, { take: 100 })),
            take(1),
        ).subscribe(({ discountedProducts }) => {
            this.variantDiscounts = {};
            if (this.product && discountedProducts) {
                const match = discountedProducts.find((item: any) => item.product.id === this.product!.id);
                if (match) {
                    for (const dv of match.discountedVariants || []) {
                        this.variantDiscounts[dv.variantId] = dv.discountPercentage;
                    }
                }
            }
        });

        // Subscribe to central wishlist state
        this.wishlistSub = this.wishlistService.wishlistIds$.subscribe(ids => {
            if (this.selectedVariant) {
                this.inWishlist = ids.has(this.selectedVariant.id);
            }
        });

        // Subscribe to compare state
        this.compareSub = this.compareService.compareIds$.subscribe(ids => {
            this.compareCount = ids.length;
            this.inCompare = this.product ? ids.includes(this.product.id) : false;
            this.cdr.markForCheck();
        });

        this.stateService.select(state => state.signedIn).subscribe(s => {
            this.signedIn = s;
        });

        // relatedProducts$ is set inside the product subscription to use the collection slug

        this.activeService.activeOrder$.subscribe(order => {
            this.qtyInCart = {};
            this.orderLineIds = {};
            for (const line of order?.lines ?? []) {
                this.qtyInCart[line.productVariant.id] = line.quantity;
                this.orderLineIds[line.productVariant.id] = line.id;
            }
        })
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        if (this.wishlistSub) {
            this.wishlistSub.unsubscribe();
        }
        if (this.compareSub) {
            this.compareSub.unsubscribe();
        }
    }

    toggleInstallation() {
        this.installationSelected = !this.installationSelected;
    }

    openAddToCartDialog(variant: Variant) {
        this.modalService.fromComponent(AddToCartDialogComponent, {
            locals: {
                variant,
                product: this.product,
                installationSelected: this.installationSelected,
                installationPrice: Number((this.product as any)?.customFields?.installationPrice) || 0,
                variantDiscounts: this.variantDiscounts,
            },
        }).subscribe(result => {
            if (result?.qty) {
                this.addToCart(variant, result.qty);
            }
        });
    }

    addToCart(variant: Variant, qty: number) {
        this.inFlightVariantId = variant.id;
        const useInstallation = this.installationSelected && (this.product as any)?.customFields?.installationAvailable;
        const mutation = useInstallation ? ADD_TO_CART_WITH_INSTALLATION : ADD_TO_CART;
        const variables: any = { variantId: variant.id, qty };
        if (useInstallation) {
            variables.customFields = { installationRequested: true };
        }
        this.dataService.mutate<AddToCartMutation, AddToCartMutationVariables>(mutation, variables).subscribe(({addItemToOrder}) => {
            this.inFlightVariantId = null;
            switch (addItemToOrder.__typename) {
                case 'Order':
                    this.stateService.setState('activeOrderId', addItemToOrder ? addItemToOrder.id : null);
                    if (variant) {
                        this.notificationService.notify({
                            title: this.translateService.instant('cart.addedToCart'),
                            type: 'info',
                            duration: 3000,
                            templateRef: this.addToCartTemplate,
                            templateContext: {
                                variant,
                                quantity: qty,
                            },
                        }).subscribe();
                    }
                    break;
                case 'OrderModificationError':
                case 'OrderLimitError':
                case 'NegativeQuantityError':
                case 'InsufficientStockError':
                    this.notificationService.error(addItemToOrder.message).subscribe();
                    break;
            }

        });
    }

    adjustQuantity(variant: Variant, delta: number) {
        const currentQty = this.qtyInCart[variant.id] || 0;
        const newQty = currentQty + delta;
        const lineId = this.orderLineIds[variant.id];
        if (!lineId) return;
        this.inFlightVariantId = variant.id;
        if (newQty <= 0) {
            this.dataService.mutate<RemoveItemFromCartMutation, RemoveItemFromCartMutationVariables>(
                REMOVE_ITEM_FROM_CART, { id: lineId }
            ).subscribe(() => { this.inFlightVariantId = null; });
        } else {
            this.dataService.mutate<AdjustItemQuantityMutation, AdjustItemQuantityMutationVariables>(
                ADJUST_ITEM_QUANTITY, { id: lineId, qty: newQty }
            ).subscribe(() => { this.inFlightVariantId = null; });
        }
    }

    toggleWishlist() {
        if (!this.signedIn || !this.selectedVariant) {
            return;
        }
        this.wishlistService.toggleWishlist(this.selectedVariant.id).subscribe();
    }

    toggleCompare() {
        if (!this.product) {
            return;
        }
        const productId = this.product.id;
        const alreadyHas = this.compareService.has(productId);
        if (!alreadyHas && this.compareService.count >= CompareService.MAX) {
            this.notificationService
                .error(this.translateService.instant('compare.limitReached'))
                .subscribe();
            return;
        }
        if (!alreadyHas) {
            this.compareService.toggle(productId);
        }
        this.modalService
            .fromComponent(CompareDialogComponent, {
                locals: {
                    firstProduct: {
                        id: productId,
                        name: this.product.name,
                        slug: this.route.snapshot.paramMap.get('slug') || '',
                        asset: this.product.featuredAsset ?? null,
                    },
                },
            })
            .subscribe();
    }

    viewCartFromNotification(closeFn: () => void) {
        this.stateService.setState('cartDrawerOpen', true);
        closeFn();
    }

    private parseSpecs(raw: any): Array<{ category: string; specs: Array<{ name: string; value: string }> }> {
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].category !== undefined) {
                return parsed;
            } else if (Array.isArray(parsed) && parsed.length > 0) {
                return [{ category: 'General', specs: parsed }];
            }
        } catch {}
        return [];
    }

    getOptionValue(variant: Variant, groupId: string): string {
        const option = variant.options.find((o: any) => o.group?.id === groupId);
        return option ? option.name : '-';
    }

    isOutOfStock(variant: Variant): boolean {
        const level = (variant as any).stockLevel;
        if (level == null) return false;
        if (typeof level === 'string') {
            if (level === 'OUT_OF_STOCK') return true;
            const num = Number(level);
            if (!isNaN(num)) return num <= 0;
        }
        return false;
    }

    getUniqueOptionValues(groupId: string): string[] {
        if (!this.product) return [];
        const values = new Set<string>();
        for (const variant of this.product.variants) {
            const val = this.getOptionValue(variant, groupId);
            if (val !== '-') values.add(val);
        }
        return Array.from(values).sort();
    }

    get filteredVariants(): Variant[] {
        if (!this.product) return [];
        return this.product.variants.filter(variant => {
            for (const groupId of Object.keys(this.variantFilters)) {
                const filterVal = this.variantFilters[groupId];
                if (!filterVal) continue;
                const optionVal = this.getOptionValue(variant, groupId);
                if (optionVal !== filterVal) return false;
            }
            return true;
        });
    }

    trackByProductId(index: number, item: any): string {
        return item.productId;
    }

    private getMostRelevantCollection(collections: Collection[], lastCollectionSlug: string | null) {
        const lastCollection = collections.find(c => c.slug === lastCollectionSlug);
        if (lastCollection) {
            return lastCollection;
        }
        return collections.slice().sort((a, b) => {
            if (a.breadcrumbs.length < b.breadcrumbs.length) {
                return 1;
            }
            if (a.breadcrumbs.length > b.breadcrumbs.length) {
                return -1;
            }
            return 0;
        })[0];
    }

}
